/** @jsx jsx */

import { useState } from 'react';

import { Border } from '@zapier/theme';
import { css, jsx } from '@emotion/core';
import { sortVersionStrings } from '@zapier/product-dev-platform';

import ItemRow from '../ItemRow';
import ItemVersionRow from '../ItemVersionRow';

import {
  IntegrationType,
  ItemValue,
  onDeleteCallback,
  onViewCallback,
  onEditCallback,
} from '../types';
import { Text, Tooltip } from '@zapier/design-system';
import { useLocalStorage } from '@zapier/common-hooks';

import { DISABLED_INTEGRATIONS_KEY } from './constants';

const Styles = {
  root: css`
    position: relative;
    :not(:last-child) {
      border-bottom: 1px solid ${Border.color.default};
    }
  `,
};

type Props = {
  integration: IntegrationType;
  isIntegrationAdmin: boolean;
  isLoadingActions: boolean;
  onDelete: onDeleteCallback;
  onClickToggle: () => void;
  onEdit: onEditCallback;
  onView: onViewCallback;
};

function TableBody(props: Props) {
  const {
    integration,
    isIntegrationAdmin,
    isLoadingActions,
    onClickToggle,
    onDelete,
    onEdit,
    onView,
  } = props;

  const integrationVersion = integration.version;

  const [isShowingVersions, setIsShowingVersions] = useState(false);

  const [disabledIntegrations, setDisabledIntegrations] = useLocalStorage(
    DISABLED_INTEGRATIONS_KEY
  );
  const [isHovered, setIsHovered] = useState(false);

  const onChoose = async (value: ItemValue) => {
    if (disabledIntegrations && disabledIntegrations[integration.id]) {
      return;
    }
    switch (value) {
      case 'view':
        onView({
          id: integration.id,
          isBeta: integration.isBeta,
          isPublic: integration.isPublic,
          title: integration.title,
          version: integrationVersion,
        });
        break;
      case 'edit':
        onEdit({
          id: integration.id,
          title: integration.title,
          version: integrationVersion,
        });
        break;
      case 'show-versions':
        setIsShowingVersions(true);
        break;
      case 'hide-versions':
        setIsShowingVersions(false);
        break;
      case 'delete': {
        const res = await onDelete({
          id: integration.id,
          ...(integration.versions.length > 1
            ? { version: integrationVersion }
            : {}),
        });
        if (res?.isPendingDeletion) {
          setDisabledIntegrations({
            ...disabledIntegrations,
            [integration.id]: true,
          });
        }
        break;
      }
    }
  };

  const versions: Array<string> = [];
  const integrationTyped = (integration as unknown) as IntegrationType;
  versions.push(
    ...(integrationTyped.versions || []).filter(
      version => version !== integrationTyped.version
    )
  );

  return (
    <tbody
      css={Styles.root}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isHovered &&
        disabledIntegrations &&
        disabledIntegrations[integration.id] && (
          <Tooltip allowMultiline={true}>
            <Text color="inherit" type="smallPrint1Semibold">
              Integration version is scheduled for deletion
            </Text>
          </Tooltip>
        )}
      <ItemRow
        integration={integration}
        isDisabled={disabledIntegrations?.[integration.id] ?? false}
        isIntegrationAdmin={isIntegrationAdmin}
        isLoadingActions={isLoadingActions}
        isShowingVersions={isShowingVersions}
        isVersion={false}
        onChoose={onChoose}
        onClickToggle={onClickToggle}
      />
      {isShowingVersions &&
        sortVersionStrings(versions).map(version => (
          <ItemVersionRow
            integration={integration}
            isIntegrationAdmin={isIntegrationAdmin}
            isLoadingActions={isLoadingActions}
            key={version}
            onDelete={onDelete}
            onEdit={onEdit}
            version={version}
          />
        ))}
    </tbody>
  );
}

export default TableBody;
